// Number formatting shared by the Run mode table and details rail. Kept out
// of the component files so Fast Refresh keeps working (a .tsx that exports
// non-components reloads the whole module) and so the two surfaces cannot
// drift on how they print the same summary.

import { format, formatDistance } from 'date-fns'

export const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  signDisplay: 'always',
  maximumFractionDigits: 2,
})
/** An amount of money, such as starting cash: unsigned, cents only when there are some. */
export const CASH = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})
export const PERCENT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
export const COUNT = new Intl.NumberFormat('en-US')

/** Placeholder for a metric the run has not produced. */
export const NO_VALUE = '-'

export function formatSummaryNumber(
  value: number | null | undefined,
  formatter: Intl.NumberFormat,
): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatter.format(value) : NO_VALUE
}

export function formatProfitFactor(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(2)}x` : NO_VALUE
}

/**
 * "2 hours" / "~2 hours ago" for an ISO timestamp, or null when the value is
 * missing or not a date. date-fns throws RangeError "Invalid time value" on an
 * invalid Date, and several of these fields are legitimately empty (a
 * universe that has never resolved has `resolvedAt: ""`), so every relative
 * time on the page goes through this guard rather than calling date-fns raw.
 *
 * date-fns hedges with "about" ("about 22 hours ago"), which is the widest
 * word in a Triggered column or a run-picker row. It becomes a tilde
 * (Chris, 2026-09-06), so every consumer of this helper saves the space.
 */
export function relativeTime(
  iso: string | null | undefined,
  options?: { addSuffix?: boolean },
  now: number = Date.now(),
): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return formatDistance(date, now, options).replace(/^about /, '~')
}

/** "2024-01-2" or "2024-01-02" as a local Date, or null when it is not one. */
function parseDayString(value: string): Date | null {
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * The span of days a set of rows covers: "Jan 2 to Sep 4, 2026", or with
 * both years when they differ, or one date when the rows share a day. Null
 * without a parseable date.
 */
export function dateRangeLabel(days: string[]): string | null {
  const dates = days.map(parseDayString).filter((d): d is Date => d !== null)
  if (dates.length === 0) return null
  let first = dates[0]
  let last = dates[0]
  for (const d of dates) {
    if (d < first) first = d
    if (d > last) last = d
  }
  if (first.getTime() === last.getTime()) return format(first, 'MMM d, yyyy')
  if (first.getFullYear() === last.getFullYear()) {
    return `${format(first, 'MMM d')} to ${format(last, 'MMM d, yyyy')}`
  }
  return `${format(first, 'MMM d, yyyy')} to ${format(last, 'MMM d, yyyy')}`
}

/** A local calendar date for an ISO timestamp, or null when it is not a date. */
export function calendarDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString()
}
